import CompanyService from "@/store/services/company.service.js";
import UserService from "@/store/services/user.service.js";
import UserRightsService from "@/store/services/userrights.service";
import InvoiceService from "@/store/services/invoice.service";
import GekkoService from "@/store/services/gekko.service.js"

export default {
  namespaced: true,
  state: {
    userData: {},
    currencyOptions: [],
    currentCompany: {},
    currentUser: {},
    currentUserProfile: {},
    currentUserRights: {},
    currentUserAccessRights: {},
    userCompanies: [],
    availableCompanies: {},
    invoiceLanguageOptions: {},
    ueCountryCodes: ['BE', 'BG', 'CZ', 'DK', 'DE', 'EE', 'IE', 'GR', 'ES', 'FR', 'HR', 'IT', 'CY', 'LV', 'LT', 'LU', 'HU', 'MT', 'AT', 'PL', 'PT', 'RO', 'SI', 'SK', 'FI', 'SE'],
    visitingCompanyId: null,
    countryOptions: [],
    companyRequiredData: null,
    mobileAppView: false,
    mobileApp: "",                  // platform: [web, costs, hours, trips, invoices, full ]
    waitingForResponse: false,
    visitingStatus: false
  },
  getters: {
    getUserId: state => state.userData.user_id,
    getCompanyId: state => {
      if (state.visitingCompanyId) {
        return state.visitingCompanyId
      } else {
        return state.userData.company_id
      }
    },
    getIsStaff: state => state.userData.is_staff,
    getMobileAppView: state => state.mobileAppView,
    getMobileApp: state => state.mobileApp,
    getCurrentCompany: state => state.currentCompany,
    getCurrentUser: state => state.currentUser,
    getCurrentUserProfile: state => state.currentUserProfile,
    getPreferredLanguage: state => {
      return state.currentUserProfile ? state.currentUserProfile.preferred_language : 'EN'
    },
    getCurrentRights: state => state.currentUserRights,
    getCurrentAccessRights: state => state.currentUserAccessRights,
    getCompanyList: state => {
      return state.userCompanies.filter(company => company.is_owner)
    },
    getSharedCompanyList: state => {
      return state.userCompanies.filter(company => !company.is_owner)
    },
    getInvoiceLanguageOptions: state => state.invoiceLanguageOptions,
    getInvoiceLanguageOptionsByCode: state => lang_code => state.invoiceLanguageOptions.find(lang => lang.value === lang_code),
    getUeCountryCodes: state => state.ueCountryCodes,
    getAvailableCompanies: state => state.availableCompanies,
    getCurrencyOptions: state => state.currencyOptions,
    getCurrencyByCode: state => currency_code => state.currencyOptions.find(item => item.code === currency_code),
    getCountryOptions: state => state.countryOptions,
    getCompanyRequiredData: state => state.companyRequiredData,
    getCompanyMissingFields: state => {
      let missing_fields = []
      state.companyRequiredData.forEach(dataItem => {
        if (!state.currentCompany[dataItem.key]) {
          missing_fields.push(dataItem)
        }
      })
      return missing_fields
    },
    getCompanyBelongsToUser: state => (state.currentCompany.created_by.id === state.currentUser.id),
    isWaitingForResponse: state => state.waitingForResponse,
    isVisiting: state => state.visitingStatus
  },
  mutations: {
    SET_USER_DATA (state, data) {
      state.userData = data
    },
    SET_COMPANY_ID (state, company_id) {
      state.userData.company_id = company_id
    },
    SET_CURRENT_COMPANY (state, company) {
      state.currentCompany = company
    },
    SET_CURRENT_COMPANY_SETTINGS (state, companySettings) {
      state.currentCompany.companysettings = companySettings
    },
    SET_CURRENT_COMPANY_LOGO (state, payload) {
      state.currentCompany.logo = payload.logo
      state.currentCompany.logo_thumbnail = payload.logo_thumbnail
    },
    SET_CURRENT_USER (state, user) {
      state.currentUser = user
    },
    SET_CURRENT_USER_EMAIL (state, email) {
      state.currentUser.email = email
    },
    SET_CURRENT_USER_EMAIL_VALIDATED (state, validated) {
      state.currentUser.profile.active_email = validated
    },
    SET_CURRENT_USER_PROFILE (state, userProfile) {
      state.currentUserProfile = userProfile
    },
    SET_CURRENT_USER_AVATAR (state, avatar) {
      state.currentUser.profile.avatar = avatar
    },
    SET_CURRENT_USER_NAME (state, name) {
      state.currentUser.display_name = name
      state.currentUser.profile.full_name = name
    },
    SET_CURRENT_USER_RIGHTS (state, currentUserRights) {
      state.currentUserRights = currentUserRights
    },
    SET_CURRENT_USER_ACCESS_RIGHTS (state, currentUserAccessRights) {
      state.currentUserAccessRights = currentUserAccessRights
    },
    SET_INVOICE_LANGUAGE_OPTIONS (state, options) {
      state.invoiceLanguageOptions = options
    },
    SET_VISITING_COMPANY_ID (state, visitingCompanyId) {
      state.visitingCompanyId = visitingCompanyId
    },
    SET_USER_COMPANIES (state, companies) {
      state.userCompanies = companies
    },
    SET_AVAILABLE_COMPANIES (state, companies) {
      state.availableCompanies = companies
    },
    SET_CURRENCY_OPTIONS (state, currencies) {
      state.currencyOptions = currencies
    },
    SET_COUNTRY_OPTIONS (state, countries) {
      state.countryOptions = countries
    },
    SET_INCREMENT_CURRENT_USER_AI_COUNTER (state) {
      state.currentUser.profile.ai_image_counter++
    },
    SET_CONSTANTS (state) {
      // set company default required data
      state.companyRequiredData = [
        { key: 'address', label: this.getters.getRoot.$gettext('Address') },
        // { key: 'bank_account_number', label: this.getters.getRoot.$gettext('Bank account number') },
        // { key: 'btw', label: this.getters.getRoot.$gettext('Btw number') },
        { key: 'city', label: this.getters.getRoot.$gettext('City') },
        { key: 'country', label: this.getters.getRoot.$gettext('Country') },
        // { key: 'email', label: this.getters.getRoot.$gettext('Email') },
        // { key: 'legal_form', label: this.getters.getRoot.$gettext('Legal form') },
        { key: 'name', label: this.getters.getRoot.$gettext('Name') },
        // { key: 'phone_number', label: this.getters.getRoot.$gettext('Phone number') },
        // { key: 'postal_code', label: this.getters.getRoot.$gettext('Postal code') },
        // { key: 'kvk', label: state.currentCompany.coc_label_short }
      ]
      // require kvk field only for NL BE and ES
      if (state.currentCompany.country === 'NL' || state.currentCompany.country === 'BE' || state.currentCompany.country === 'ES') {
        state.companyRequiredData.push({ key: 'kvk', label: state.currentCompany.coc_label_short })
      }
    },
    SET_MOBILE_APP_VIEW (state, value) {
      state.mobileAppView = value
    },
    SET_MOBILE_APP (state, value) {
      state.mobileApp = value
    },
    SET_PREFERRED_LANGUAGE (state, lang) {
      state.currentUserProfile.preferred_language = lang
    },
    SET_WAITING_FOR_RESPONSE (state, value) {
      state.waitingForResponse = value
    },
    LOAD_VISITING_COOKIE (state) {
      const cookie = this.getters.getRoot.$cookies.get('gk-acc-vcid');
      state.visitingStatus = cookie ? cookie : false;
    },
    SET_VISITING_COOKIE (state, value) {
      var d1 = new Date (), expire = new Date ( d1 );
      expire.setDate(d1.getDate() + 400); // 400 days max-age https://chromestatus.com/feature/4887741241229312
      if (value) {
        this.getters.getRoot.$cookies.set('gk-acc-vcid', value, expire, '/');
        state.visitingStatus = value
      } else {
        this.getters.getRoot.$cookies.remove('gk-acc-vcid', '/');
        state.visitingStatus = false
      }
    },
  },
  actions: {
    setUserData ({ commit }, data) {
      commit('SET_USER_DATA', data)
    },
    switchCompanyProfile ({ commit }, company_id) {
      localStorage.setItem('company_id', company_id)
      commit('SET_COMPANY_ID', company_id)
    },
    switchToVisitingCompany ({ commit }, company_id) {
      commit('SET_COMPANY_ID', company_id)
      commit('SET_VISITING_COOKIE', company_id)
    },
    setMobileAppView ({ commit }, value) {
      commit('SET_MOBILE_APP_VIEW', value)
    },
    setMobileApp ({ commit }, value) {
      commit('SET_MOBILE_APP', value)
    },
    loadCurrentUser ({ commit }, params) {
      return UserService.retrieveCurrent(params).then((response) => {
        commit('SET_CURRENT_USER', response.data)
        commit('SET_CURRENT_USER_PROFILE', response.data.profile)
      })
    },
    loadCurrentCompany ({ commit, dispatch }) {
      return CompanyService.getCurrent().then((response) => {
        commit('SET_CURRENT_COMPANY', response.data)
        const company_id = response.data.id
        return Promise.all([
          dispatch('loadCompanyUserRights', company_id ),
          dispatch('loadCompanyAccessRights'),
          dispatch('brands/loadCompanyBrands', company_id, { root: true })
        ])
      }).catch((error) => {
        // TODO
        // could be that a user is trying to see a company that
        // is no longer connected or lost viewing rights, redirect to dashboard
      })
    },
    refreshCompanyData ({ commit }) {
      return CompanyService.getCurrent().then((response) => {
        commit('SET_CURRENT_COMPANY', response.data)
      })
    },
    loadInvoiceLanguageOptions ({ commit }) {
      InvoiceService.getLanguageOptions().then((response) => {
        const languages = response.data.map(language => { return { value: language[0], text: language[1] } })
        commit('SET_INVOICE_LANGUAGE_OPTIONS', languages)
      })
    },
    loadCompanyUserRights ({ commit }, company_id ) {
      return UserRightsService.retrieveCurrent({ company_profile: company_id }).then((response) => {
        commit('SET_CURRENT_USER_RIGHTS', response.data)
      })
    },
    loadCompanyAccessRights ({ commit }) {
      return CompanyService.getAccessRights().then((response) => {
        commit('SET_CURRENT_USER_ACCESS_RIGHTS', response.data)
      })
    },
    loadUserCompanies ({ commit }) {
      CompanyService.list().then((response) => {
        commit('SET_USER_COMPANIES', response.data)
      })
    },
    loadAvailableCompanies ({ commit }) {
      return CompanyService.companyPickerOptions().then((response) => {
        commit('SET_AVAILABLE_COMPANIES', {
          profile_companies: response.data['profile_companies'].map(item => item[0]),
          accounting_clients: response.data['accounting_clients'].map(item => item[0])
        })
      })
    },
    loadCurrencyOptions ({ commit }) {
      return GekkoService.getCurrencyOptions().then((response) => {
        commit('SET_CURRENCY_OPTIONS', response)
      })
    },
    loadCountries ({ commit }) {
      return CompanyService.getCountries().then((response) => {
        commit('SET_COUNTRY_OPTIONS', response.data)
      })
    },
    uploadCompanyLogo ({ commit }, payload) {
      const data = { file: payload.logo }
      return CompanyService.uploadLogo(payload.id, data).then((response) => {
        commit('SET_CURRENT_COMPANY_LOGO', { logo: response.data.logo, logo_thumbnail: response.data.logo_thumbnail })
      })
    },
    deleteCompanyLogo ({ commit }, company_id) {
      return CompanyService.deleteLogo(company_id).then(() => {
        commit('SET_CURRENT_COMPANY_LOGO', { logo: null, logo_thumbnail: null })
      })
    },
    loadMainGlobalData ({ dispatch }) {
      return Promise.all([
        dispatch('loadCurrentUser', { extended: 'True' }),
        dispatch('loadCurrentCompany'),
        dispatch('loadInvoiceLanguageOptions'),
        dispatch('loadAvailableCompanies'),
        dispatch('loadCurrencyOptions'),
        dispatch('loadCountries')
      ])
    }
  }
}
